import React from 'react'
import first from 'lodash/first'
import keys from 'lodash/keys'
import merge from 'lodash/merge'
import { NetworkStatus } from '@apollo/client'
import type { HeaderCellProps } from './types'
import { DirectionEnum } from '@ztechapps/common.contract'
import { HorizontalStack, Icon, Link, ShowIf, Spinner } from '@ztechapps/webui.util'
import { useTableContext } from '../Context'
import { useMongoSort } from '../../hooks/useMongoSort'
import { ColumnResizeHandle } from '../../ColumnResizeHandle/ColumnResizeHandle'
import { HeaderCell } from './HeaderCell'
import { useColumResize } from '../hooks/useColumResize'
import { TableFilter } from '../table-filter'

export type SorterHeaderCellProps = HeaderCellProps

export const ActionHeaderCell = ({ children, style, field, ...rest }: SorterHeaderCellProps) => {
  const {
    listResult: { networkStatus, onSort, sort, loading: loadingCore },
  } = useTableContext()

  const canSort = !field?.excludeOnSort
  const canFilter = !field?.excludeOnFilter
  const fieldName = field?.name

  const { width, onStart } = useColumResize(field)

  style = merge({}, style, { width: width })

  const sortFunc = useMongoSort({ onSort })

  //get the key of the first item of sort
  const currentSortField = first(keys(sort))

  //the loading happens only when setVariables, no need to trigger on refetch initial load
  const loading = [NetworkStatus.setVariables].includes(networkStatus)

  //get the first value of sort
  const sortAscending = sort[currentSortField] === 1
  const sortIcon = sortAscending ? 'sort-alpha-asc' : 'sort-alpha-desc'

  const handleSort = (selectedFieldName?: string) => {
    if (!selectedFieldName) return //we ignore the sort if there is no name
    if (!canSort) return //we just ignore the sorting if we can't sort
    if (loadingCore) return //we skip the sort if we are loading

    //now we toggle it
    const direction = sortAscending ? DirectionEnum.desc : DirectionEnum.asc

    return sortFunc({ direction, activeSortField: { name: selectedFieldName } })
  }

  let spinnerOrIconContent = null
  if (currentSortField === fieldName) {
    spinnerOrIconContent = <Icon name={sortIcon} />
    if (loading) spinnerOrIconContent = <Spinner size="sm" className="me-1" />
  }

  const onClick = () => handleSort(fieldName)

  return (
    <HeaderCell {...rest} style={style} field={field}>
      <HorizontalStack className="justify-content-between align-items-end">
        {!canSort ? (
          children
        ) : (
          <Link onClick={onClick} disabled={loadingCore} className="py-0 m-0">
            <span>
              {spinnerOrIconContent}
              {children}
            </span>
          </Link>
        )}
        <HorizontalStack gap={2}>
          <ShowIf condition={canFilter}>
            <TableFilter field={field} />
          </ShowIf>
          <ColumnResizeHandle onMouseDown={onStart} />
        </HorizontalStack>
      </HorizontalStack>
    </HeaderCell>
  )
}
